import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type { ComponentProps } from "react";
import cc from "classcat";
import {
  TextInput,
  Button,
  RadioButtons,
  Tooltip,
  Drawer,
  Alert,
} from "@narmi/design_system";
import Payee from "byzantine/src/Payee";
import type { NewPayeeProps } from "byzantine/src/Payee";
import Address from "byzantine/src/Address";
import { useLocalization } from "@fluent/react";
import {
  extractIgnoredWarnings,
  getWarningMessage,
} from "cerulean/src/FormReviewWarningsContext/FormReviewWarningsContext";

import {
  ContextForm,
  useLoadingContext,
  useSetChatVisibility,
  PhoneTextInput,
  useFormData,
  EmailFormField,
  PasswordTextInput,
} from "cerulean";
import { useTriggerGuideForceReset } from "../../src/modules/guides/hooks";
import UserFeaturesContext from "../contexts/UserFeaturesContext";
import CountrySelector from "../form/CountrySelector";
import BankingUSAddressField from "../address/BankingUSAddressField";
import DrawerLayout from "../DrawerLayout";
import styles from "./AddPayeeDrawer.module.scss";

type AddPayeeDrawerProps = {
  open: boolean;
  handleClose: () => void;
  updatePayees: (payee: Payee) => void;
};

type PayeeFormData = Partial<Omit<NewPayeeProps, "type" | "address">> &
  Partial<API.Address>;

const AddPayeeDrawer = ({
  updatePayees,
  handleClose,
  open,
}: AddPayeeDrawerProps) => {
  const { l10n } = useLocalization();
  const { isLoading, setIsLoading } = useLoadingContext();
  const drawerId = useId();
  useSetChatVisibility(drawerId, !open);
  const { formData, setFormData, onChange } = useFormData<PayeeFormData>({});
  const [payeeType, setPayeeType] = useState<Payee["type"] | "">(
    Payee.TYPE.BUSINESS
  );
  const { disable_billpay_create_person_payee: disablePersonalPayee } =
    useContext(UserFeaturesContext);
  const triggerGuideForceReset = useTriggerGuideForceReset();
  const [warningMessage, setWarningMessage] = useState("");
  const warningRef = useRef<HTMLDivElement>(null);
  const hasOverride = Boolean(formData.ignore_warnings?.length);
  const submitLabel = hasOverride
    ? l10n.getString("add-payee-override-submit")
    : l10n.getString("add-payee-submit");

  const clearWarning = useCallback(() => {
    setWarningMessage("");
    onChange({ ignore_warnings: undefined });
  }, [onChange]);

  useEffect(() => {
    if (!open) clearWarning();
  }, [open, clearWarning]);

  useEffect(() => {
    warningRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [warningMessage]);

  const clearForm = () => {
    setFormData({});
    setWarningMessage("");
  };

  const payeeCreateOptions: Record<string, string> = {
    [l10n.getString("label-business")]: Payee.TYPE.BUSINESS,
  };
  if (!disablePersonalPayee) {
    payeeCreateOptions[l10n.getString("label-personal")] = Payee.TYPE.PERSON;
  }

  const onSubmit = (callback: (error?: unknown) => void = () => {}) => {
    const payload = {
      type: payeeType,
      name: formData.name,
      description: formData.description,
      phone: formData.phone,
      email: formData.email,
      passphrase: formData.passphrase,
      address: {
        street_address: formData.street_address,
        street_address_2: formData.street_address_2,
        city: formData.city,
        region_code: formData.region_code,
        postal_code: formData.postal_code,
        country_code: formData.country_code,
      },
      account_number: formData.account_number,
      ignore_warnings: formData.ignore_warnings,
    };
    // We only want to validate the address if the payee is a business
    if (payload.type !== Payee.TYPE.PERSON) {
      const address: Address = new Address(payload.address);
      try {
        address.validate();
      } catch (error) {
        callback(l10n.getString("add-payee-address-required"));
        return;
      }
    }
    // ContextForm validates required fields before invoking this action. The
    // empty type after success is intentional existing reset behavior.
    const newPayee = new Payee(payload as NewPayeeProps);
    // DrawerLayout's ContextForm action delegates loading to its caller.
    setIsLoading(true);
    clearWarning();
    newPayee
      .submit()
      .then((response: { payee?: Parameters<typeof Payee.deserialize>[0] }) => {
        if (response.payee) {
          const deserializedPayee = Payee.deserialize(response.payee);
          updatePayees(deserializedPayee);
          triggerGuideForceReset();
          callback();
          handleClose();
          // we want to reset the form if payee add is successful
          setFormData({});
          setPayeeType(Payee.TYPE.BUSINESS);
        }
      })
      .catch((error: unknown) => {
        const warnings = extractIgnoredWarnings(error);
        if (!warnings.length) {
          callback(error);
          return;
        }
        onChange({ ignore_warnings: warnings });
        setWarningMessage(`${getWarningMessage(warnings)}`);
        // Complete the action without asking ContextForm to also emit a toast.
        callback();
      })
      .finally(() => setIsLoading(false));
  };

  const descriptionLabel = () => {
    if (payeeType === Payee.TYPE.PERSON) {
      return l10n.getString("textinput-label-nickname");
    }
    return l10n.getString("add-payee-description");
  };

  return (
    <Drawer
      isOpen={open}
      onUserDismiss={handleClose}
      position="right"
      depth="633px"
      paddingSize="none"
      showControls={false}
      testId="add-payee-drawer"
    >
      <div className={styles.formRegion}>
        <ContextForm
          key={String(open)}
          nativeForm={false}
          data={formData}
          onChange={onChange}
          confirmWithToastMessage={true}
        >
          <DrawerLayout
            isContextForm
            isSaveDisabled={isLoading}
            onSave={onSubmit as ComponentProps<typeof DrawerLayout>["onSave"]}
            saveLabel={submitLabel}
            onCancel={handleClose}
            cancelLabel={l10n.getString("button-cancel")}
          >
            {warningMessage && (
              <div ref={warningRef} className={styles.warning}>
                <Alert
                  kind="warn"
                  isActive
                  isDismissable
                  onUserDismiss={clearWarning}
                >
                  <p>{warningMessage}</p>
                  <Button
                    kind="plain"
                    size="s"
                    type="button"
                    label={l10n.getString("add-payee-clear-form")}
                    onClick={clearForm}
                  />
                </Alert>
              </div>
            )}
            <div className="form-section-header">
              {l10n.getString("add-payee-type")}
            </div>
            <div className="payee-select-container">
              <RadioButtons
                initialValue={Payee.TYPE.BUSINESS}
                name="payeeType"
                onChange={(e) => setPayeeType(e.target.value as Payee["type"])}
                value={payeeType}
                options={payeeCreateOptions}
              />
            </div>
            <div
              className={cc([
                "form-section-header",
                "margin--top--xl",
                { "margin--bottom--xs": payeeType !== Payee.TYPE.PERSON },
              ])}
            >
              {l10n.getString("add-payee-details")}
            </div>
            {payeeType !== Payee.TYPE.PERSON && (
              <div className="fontSize--s fontColor--secondary margin--bottom--l">
                {l10n.getString("add-payee-merchant-instructions")}
              </div>
            )}
            <ContextForm.Field required>
              <TextInput field="name" label={l10n.getString("label-name")} />
            </ContextForm.Field>
            <ContextForm.Field required>
              <TextInput field="description" label={descriptionLabel()} />
            </ContextForm.Field>
            {payeeType === Payee.TYPE.BUSINESS ? (
              <div className="has-tooltip-child">
                <ContextForm.Field required>
                  <TextInput
                    field="account_number"
                    label={l10n.getString("add-payee-account-number")}
                  />
                  <div className="tooltip-info-icon">
                    <Tooltip
                      text={l10n.getString("add-payee-account-number-help")}
                    >
                      <span className="narmi-icon-info"></span>
                    </Tooltip>
                  </div>
                </ContextForm.Field>
              </div>
            ) : (
              <EmailFormField required />
            )}
            <ContextForm.Field required>
              <PhoneTextInput field="phone" />
            </ContextForm.Field>
            {payeeType === Payee.TYPE.BUSINESS && (
              <>
                <BankingUSAddressField
                  onUpdate={onChange}
                  data={formData}
                  showTitle={false}
                />
                <div className="margin--top--l">
                  <ContextForm.Field required>
                    <CountrySelector
                      label={l10n.getString("add-payee-country")}
                      field="country_code"
                      countries={Payee.GEO_COUNTRIES}
                    />
                  </ContextForm.Field>
                </div>
              </>
            )}
            {payeeType === Payee.TYPE.PERSON && (
              <>
                <div className="form-section-header margin--bottom--xs">
                  {l10n.getString("add-payee-secret-phrase")}
                </div>
                <div className="fontSize--m margin--bottom--l">
                  {l10n.getString("add-payee-secret-phrase-help")}
                </div>
                <ContextForm.Field style={{ marginBottom: 0 }} required>
                  <PasswordTextInput
                    field="passphrase"
                    name="passphrase"
                    label={l10n.getString("add-payee-secret-phrase")}
                  />
                </ContextForm.Field>
              </>
            )}
          </DrawerLayout>
        </ContextForm>
      </div>
    </Drawer>
  );
};

export default AddPayeeDrawer;
